/**
 * ArrowheadMarker — the canonical SVG arrowhead `<marker>` for inline diagrams.
 *
 * Every CSK demo that draws a directed edge hand-rolls the same `<marker>`
 * block: a `0 0 10 10` viewBox, a filled triangle `M0,0 L10,5 L0,10 z`, refY
 * centered at 5, and an `auto` / `auto-start-reverse` orientation. This is the
 * single shared home for that idiom. It renders only the `<marker>` element
 * (no wrapping `<defs>`), so it composes inside a diagram's existing `<defs>`
 * alongside gradients and glows. The caller passes the tone `color` (usually a
 * panel-theme token) and references the marker by `id` via `markerEnd`.
 *
 * For an explainer that needs the whole `<defs>` wrapper in one piece, use
 * `ArrowDefs` (which is built on this component).
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GRAY } from "../design-system/tokens.js";

export interface ArrowheadMarkerProps {
  /** Marker id referenced by edges via `markerEnd={`url(#${id})`}`. */
  readonly id: string;
  /** Fill color of the arrowhead triangle. Defaults to the neutral stroke gray. */
  readonly color?: string;
  /**
   * Reference point along the marker x-axis where the edge meets the head.
   * `9` (the default) tucks the tip flush against the line end; `8` leaves a
   * small gap for thicker strokes.
   */
  readonly refX?: number | string;
  /** Marker width and height in user units. Defaults to `7`. */
  readonly size?: number | string;
  /**
   * Marker orientation. `"auto-start-reverse"` (the default) lets one marker
   * serve both ends of bidirectional edges; `"auto"` aligns with the edge
   * direction only.
   */
  readonly orient?: string;
  /** Optional fill opacity for the arrowhead triangle (0–1). */
  readonly opacity?: number;
}

/**
 * A single `<marker>` defining a filled-triangle arrowhead. Place inside a
 * `<defs>` and reference it with `markerEnd={`url(#${id})`}`.
 */
export function ArrowheadMarker({
  id,
  color = GRAY[700],
  refX = 9,
  size = 7,
  orient = "auto-start-reverse",
  opacity,
}: ArrowheadMarkerProps): React.JSX.Element {
  return (
    <marker
      id={id}
      viewBox="0 0 10 10"
      refX={refX}
      refY={5}
      markerWidth={size}
      markerHeight={size}
      orient={orient}
    >
      <path d="M0,0 L10,5 L0,10 z" fill={color} opacity={opacity} />
    </marker>
  );
}
